import React from 'react';
import { Movie } from 'src/@types/Movie';
import { Site } from 'src/client/redux/modules/site';
import Helmet from 'src/client/containers/Helmet/Helmet';
import { secondsToRuntime } from 'src/client/utils/converters';
import { GalleryResponseModel } from 'src/@types/app';

type Props = {
  site: Site;
  item: Movie;
  contentCdnUrl: string;
  siteSubdomain: string;
};

export const MovieHelmet = (props: Props) => {
  const { contentCdnUrl, siteSubdomain, site, item } = props;

  if (!site || !item) return null;

  let title = item.metaTitle;
  const modelsCount = item.models.length;

  const models = (item.models as GalleryResponseModel[]).reduce((acc, currentItem, index) => {
    const { publishAge: debutAge, country, hair: hairColor } = currentItem;
    // let's do it starts from 1, for dummies seo guys
    acc[`model${index + 1}`] = {
      name: currentItem.name,
      debutAge,
      country: (country && country.name) || '',
      hairColor,
    };
    return acc;
  }, {});

  const photographers = item.photographers.reduce((acc, currentItem, index) => {
    // let's do it starts from 1, for dummies seo guys
    acc[`photographer${index + 1}`] = {
      name: currentItem.name,
    };
    return acc;
  }, {});

  if (!title) {
    // in case title is not set on the db
    switch (modelsCount) {
      case 1:
        title = "{{model1.name}} in '{{{movieName}}}' ({{runtime}}) - {{siteName}}";
        break;
      case 2:
        title = "{{model1.name}} & {{model2.name}} in '{{{movieName}}}' ({{runtime}}) - {{siteName}}";
        break;
      case 3:
        title = '{{model1.name}}, {{model2.name}} & {{model3.name}} video - {{siteName}}';
        break;
      default:
        title = "'{{{movieName}}}' ({{runtime}}): {{model1.name}}, {{model2.name}} and others - {{siteName}}";
        break;
    }
  }

  const thumbnailUrl = `${contentCdnUrl}/${item.siteUUID}${item.thumbnailCoverPath}`;
  const url = `https://${siteSubdomain}.${site.domainCanonical}${item.path}`;
  const contentUrl = item.files?.teasers?.includes('mp4')
    ? `${contentCdnUrl}/${item.siteUUID}/media/${item.UUID}/tease_${item.UUID}.mp4`
    : undefined;

  const secondsToISO8601 = (s: number) => `PT${Math.floor(s / 3600)}H${Math.floor((s % 3600) / 60)}M${s % 60}S`;

  return (
    <Helmet
      id={`movie-${modelsCount}`}
      defaultTitle={title}
      defaultDescription={item.metaDescription}
      forceDefaultTitle={!!item.metaTitle}
      forceDefaultDescription={!!item.metaDescription}
      variables={{
        ...models,
        ...photographers,
        runtime: secondsToRuntime(item.media.runtime),
        movieName: item.name,
        siteName: site.name,
      }}
    >
      <meta property="og:image" content={thumbnailUrl} />
      <meta property="og:type" content="video.movie" />
      <meta property="og:video" content={url} />
      <meta property="og:video:image" content={thumbnailUrl} />
      <meta property="og:video:duration" content={item.runtime.toString()} />
      <meta property="og:video:release_date" content={item.publishedAt} />
      {item.tags.map((tag) => (
        <meta key={tag} property="og:video:tag" content={tag} />
      ))}

      {contentUrl && thumbnailUrl && (
        <script type="application/ld+json">
          {`{
            "@context": "http://schema.org",
            "@type": "VideoObject",
            "name": "${item.name}",
            "description": ${JSON.stringify(item.metaDescription)},
            "thumbnailUrl": "${thumbnailUrl}",
            "uploadDate": "${item.publishedAt}",
            "contentUrl": "${contentUrl}",
            "duration": "${secondsToISO8601(item.media.runtime)}"
          }`}
        </script>
      )}

      <script>{`var isStaffSelectionGallery = ${item.isStaffSelection ? 1 : 0};`}</script>
    </Helmet>
  );
};
